/* CompactBlockerRail.jsx — iter-181 · operational blockers, horizontally.
 *
 * Compact horizontal rail of the most urgent transaction blockers
 * (NOT a dashboard widget). Each card surfaces the blocker title,
 * waiting-on owner, elapsed days, severity, and a single quick-
 * action affordance.
 *
 * Calm severity colouring — soft amber / rose / violet, never raw
 * red. Restrained shadows, subtle borders, premium SaaS feel.
 *
 * Data sources used (in priority order, never makes its own calls):
 *   • `deal.activity` — "waiting on …" / "outstanding" entries
 *   • `documents` / `docRequests` — outstanding documents
 *   • `intel.awaiting` — pre-computed operational awaiting list
 * Falls back to derived blockers from common operational signals
 * when intel hasn't loaded.
 */
import React, { useMemo } from "react";
import {
  AlertCircle, Clock, FileWarning, MessageSquare, ChevronRight,
} from "lucide-react";

const _MS_DAY = 86_400_000;

const elapsedDays = (iso) => {
  if (!iso) return 0;
  const t = typeof iso === "number" ? iso : Date.parse(iso);
  if (Number.isNaN(t)) return 0;
  return Math.max(0, Math.floor((Date.now() - t) / _MS_DAY));
};

/* Severity → calm tinted surface tones. */
const SEV = {
  high:   { ring: "rgba(244,63,94,0.22)",  bg: "rgba(254,242,242,0.7)", iconBg: "rgba(244,63,94,0.10)", iconFg: "#B91C1C", label: "High",   labelFg: "#B91C1C", strip: "#E11D48" },
  medium: { ring: "rgba(245,158,11,0.22)", bg: "rgba(255,251,235,0.7)", iconBg: "rgba(245,158,11,0.10)", iconFg: "#B45309", label: "Medium", labelFg: "#B45309", strip: "#F59E0B" },
  low:    { ring: "rgba(139,92,246,0.18)", bg: "rgba(245,243,255,0.6)", iconBg: "rgba(139,92,246,0.10)", iconFg: "#6D28D9", label: "Low",    labelFg: "#6D28D9", strip: "#8B5CF6" },
};

const sevFromDays = (days) => (days >= 7 ? "high" : days >= 3 ? "medium" : "low");

/**
 * Derive the up-to-3 most-urgent blockers from the deal payload.
 * The shape returned is normalized to:
 *   { id, title, owner, days, severity, kind }
 */
function deriveBlockers({ deal, documents = [], docRequests = [], intel }) {
  const out = [];

  // 1) Intel-provided awaiting items (most authoritative)
  const awaiting = intel?.awaiting || intel?.since_last_visit?.awaiting || [];
  awaiting.forEach((a) => {
    if (!a) return;
    const days = a.days_outstanding ?? elapsedDays(a.since || a.requested_at || a.created_at);
    out.push({
      id: a.id || `intel-${out.length}`,
      title: a.title || a.label || "Operational blocker",
      subLabel: a.subLabel || a.description || a.detail || null,
      owner: a.owner || a.waiting_on || a.party || "Counterparty",
      days,
      severity: a.severity || sevFromDays(days),
      kind: a.kind || "operational",
    });
  });

  // 2) Outstanding document requests
  if (out.length < 3) {
    (docRequests || [])
      .filter((r) => r && !["fulfilled", "rejected", "cancelled"].includes(String(r.status || "").toLowerCase()))
      .slice(0, 3 - out.length)
      .forEach((r) => {
        const days = elapsedDays(r.created_at || r.requested_at);
        out.push({
          id: `doc-${r.id}`,
          title: r.title || r.document_name || "Document outstanding",
          subLabel: r.description || "Awaiting upload from counterparty",
          owner: r.assignee_name || r.assignee || r.from || "Counterparty",
          days,
          severity: sevFromDays(days),
          kind: "document",
        });
      });
  }

  // 3) Derived from "waiting on" activity strings — last-resort, kept calm
  if (out.length < 2) {
    const acts = (deal?.activity || [])
      .filter((a) => /waiting on|outstanding|overdue|chase/i.test(a.text || a.kind || ""))
      .slice(0, 3 - out.length);
    acts.forEach((a, i) => {
      const days = elapsedDays(a.at);
      const m = /waiting on\s+([A-Z][\w\s]+)/i.exec(a.text || "");
      out.push({
        id: a.id || `act-${i}`,
        title: (a.text || "").replace(/^[•\-\s]+/, "").slice(0, 48) || "Operational blocker",
        subLabel: "Operational follow-up needed",
        owner: m ? m[1].trim() : "Counterparty",
        days,
        severity: sevFromDays(days),
        kind: "operational",
      });
    });
  }

  // De-dupe by title + owner
  const seen = new Set();
  return out
    .filter((b) => {
      const k = `${b.title}::${b.owner}`;
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    })
    .slice(0, 3);
}

/* ─── Individual blocker card (iter-183) ───────────────────────
 *  Reference hierarchy:
 *    ┌─ rose strip ┐
 *    │ ◯ icon  Title                                            │
 *    │         Sub-line                                         │
 *    │         Assignee · Sarah Johnson                         │
 *    │                                          [ 4 days ][High]│
 *    └────────────────────────────────────────────────────────  ┘
 *  Stacked vertical metadata + dual pills at the bottom — calmer
 *  scan rhythm, clearer urgency recognition than the iter-181
 *  inline layout. */
function BlockerCard({ blocker, onChase, testid }) {
  const sev = SEV[blocker.severity] || SEV.medium;
  const Icon = blocker.kind === "document" ? FileWarning : AlertCircle;
  const subLabel = blocker.subLabel || (blocker.kind === "document"
    ? "Document outstanding"
    : "Operational follow-up needed");
  return (
    <article
      className="relative rounded-xl flex-1 min-w-[260px] sm:min-w-[280px] px-3.5 pt-3 pb-3 flex items-start gap-3 overflow-hidden"
      style={{
        background: sev.bg,
        border: `1px solid ${sev.ring}`,
        boxShadow: "0 1px 2px rgba(15,23,42,0.03)",
      }}
      data-testid={testid}
    >
      {/* Severity strip on the left edge — restrained */}
      <span
        aria-hidden
        className="absolute left-0 top-0 bottom-0 w-[3px] rounded-l-xl"
        style={{ background: sev.strip }}
      />
      <span
        className="w-8 h-8 rounded-full inline-flex items-center justify-center shrink-0 mt-0.5"
        style={{ background: sev.iconBg }}
        aria-hidden
      >
        <Icon size={14} style={{ color: sev.iconFg }} strokeWidth={2.2} />
      </span>
      <div className="min-w-0 flex-1">
        <h4 className="text-[13px] font-semibold text-slate-900 leading-tight truncate">{blocker.title}</h4>
        <p className="text-[11.5px] text-slate-500 mt-0.5 leading-snug truncate">{subLabel}</p>
        <p className="text-[11.5px] text-slate-600 mt-1 truncate">
          <span className="text-slate-500">Waiting on</span>{" "}
          <span className="font-medium text-slate-800">{blocker.owner}</span>
        </p>
        {/* Dual operational pills row — duration · severity */}
        <div className="mt-2.5 flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5">
            <span
              className="text-[10.5px] font-semibold tabular-nums px-1.5 py-0.5 rounded-md leading-none"
              style={{ background: "rgba(255,255,255,0.85)", color: "#475569", border: "1px solid rgba(15,23,42,0.06)" }}
            >
              {blocker.days} {blocker.days === 1 ? "day" : "days"}
            </span>
            <span
              className="text-[10.5px] font-semibold px-1.5 py-0.5 rounded-md leading-none"
              style={{ background: sev.iconBg, color: sev.labelFg, border: `1px solid ${sev.ring}` }}
            >
              {sev.label}
            </span>
          </div>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onChase?.(blocker); }}
            className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-600 hover:text-slate-900"
            data-testid={`${testid}-chase`}
            aria-label={`Chase ${blocker.owner}`}
          >
            <MessageSquare size={11} className="text-slate-400" />
            Chase
          </button>
        </div>
      </div>
    </article>
  );
}

/* ─── Compact horizontal rail ────────────────────────────────── */
export const CompactBlockerRail = ({
  deal,
  documents = [],
  docRequests = [],
  intel,
  onChase,
  onViewAll,
  testid = "deal-room-blocker-rail",
}) => {
  const blockers = useMemo(
    () => deriveBlockers({ deal, documents, docRequests, intel }),
    [deal, documents, docRequests, intel],
  );

  if (blockers.length === 0) return null;

  return (
    <section data-testid={testid} className="space-y-2.5">
      <header className="flex items-center justify-between px-1">
        <div className="flex items-center gap-2">
          <h3 className="text-[12.5px] font-semibold text-slate-900">Current blockers</h3>
          <span
            className="text-[10.5px] font-bold tabular-nums px-1.5 min-w-[20px] h-[20px] rounded-full inline-flex items-center justify-center"
            style={{ background: "rgba(244,63,94,0.10)", color: "#B91C1C" }}
            aria-label={`${blockers.length} blockers`}
          >
            {blockers.length}
          </span>
        </div>
        {onViewAll && (
          <button
            type="button"
            onClick={onViewAll}
            className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-violet-700 hover:text-violet-900"
            data-testid={`${testid}-view-all`}
          >
            View all blockers <ChevronRight size={11} />
          </button>
        )}
      </header>
      <div
        className="flex items-stretch gap-2.5 overflow-x-auto cf-scroll snap-x snap-mandatory pb-1"
        style={{ scrollPaddingInline: "4px" }}
      >
        {blockers.map((b, i) => (
          <div key={b.id} className="snap-start flex">
            <BlockerCard blocker={b} onChase={onChase} testid={`${testid}-card-${i}`} />
          </div>
        ))}
      </div>
    </section>
  );
};

export default CompactBlockerRail;
